import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, MapPin, Layers, Search, Filter, ZoomIn, ZoomOut,
  Navigation, Users, Calendar, Clock, ChevronDown
} from 'lucide-react';
import { csvCustomers, csvJobs, zoneColors } from '../data/csvData';

const VisualPlannerPage = ({ onBack }) => {
  const [selectedZone, setSelectedZone] = useState('all');
  const [showLayers, setShowLayers] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Generate job locations on the map
  const jobLocations = useMemo(() => {
    // Create locations spread across the map area (Brighton, Burgess Hill, Hassocks area)
    return csvJobs.slice(0, 100).map((job, idx) => {
      const customer = csvCustomers.find(c => c.id === job.customerId);
      
      // Create realistic-looking clustered positions
      const clusters = [
        { name: 'Brighton', lat: 50.82, lng: -0.14, weight: 0.2 },
        { name: 'Burgess Hill', lat: 50.95, lng: -0.13, weight: 0.35 },
        { name: 'Hassocks', lat: 50.92, lng: -0.15, weight: 0.15 },
        { name: 'Haywards Heath', lat: 51.0, lng: -0.1, weight: 0.15 },
        { name: 'Hurstpierpoint', lat: 50.93, lng: -0.18, weight: 0.15 },
      ];
      
      // Pick a cluster based on index
      const cluster = clusters[idx % clusters.length];
      
      return {
        id: job.id,
        customerId: job.customerId,
        customerName: customer?.name || 'Unknown',
        zone: customer?.zone || 'Unknown',
        address: customer?.address || '',
        price: job.price || 0,
        lat: cluster.lat + (Math.random() - 0.5) * 0.06,
        lng: cluster.lng + (Math.random() - 0.5) * 0.08,
        assignee: idx % 2 === 0 ? 'Dan' : 'Steve',
        status: idx % 5 === 0 ? 'overdue' : idx % 3 === 0 ? 'today' : 'scheduled',
      };
    });
  }, []);

  // Get unique zones for filter
  const zones = useMemo(() => {
    const uniqueZones = [...new Set(jobLocations.map(j => j.zone))];
    return ['all', ...uniqueZones.filter(z => z !== 'Unknown')];
  }, [jobLocations]);

  const filteredLocations = selectedZone === 'all' 
    ? jobLocations 
    : jobLocations.filter(j => j.zone === selectedZone);

  // Convert lat/lng to percentage positions on the map
  const getMapPosition = (lat, lng) => {
    // Map bounds (approximate for Brighton/Burgess Hill area)
    const minLat = 50.78, maxLat = 51.05;
    const minLng = -0.25, maxLng = 0.0;
    
    const x = ((lng - minLng) / (maxLng - minLng)) * 100;
    const y = ((maxLat - lat) / (maxLat - minLat)) * 100;
    
    return { x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'overdue': return '#ef4444';
      case 'today': return '#22c55e';
      default: return '#3b82f6';
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-100" data-testid="visual-planner-page">
      {/* Header */}
      <div className="bg-zinc-800 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <ArrowLeft size={20} />
              </button>
            )}
            <h1 className="text-xl font-semibold">Visual Planner</h1>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <Search size={20} />
            </button>
            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <Filter size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Zone Filter */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {zones.slice(0, 10).map(zone => (
            <button
              key={zone}
              onClick={() => setSelectedZone(zone)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                selectedZone === zone 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={selectedZone === zone ? {} : { borderColor: zoneColors[zone] }}
              data-testid={`zone-filter-${zone}`}
            >
              {zone === 'all' ? 'All Zones' : zone}
            </button>
          ))}
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative overflow-hidden">
        {/* Map Background - styled to look like a real map */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(135deg, #e8f4e5 0%, #dbeafe 25%, #e8f4e5 50%, #fef3c7 75%, #e8f4e5 100%)
            `,
            transform: `scale(${zoomLevel})`,
            transition: 'transform 0.3s ease'
          }}
        >
          {/* Water features (sea on south side) */}
          <div className="absolute bottom-0 left-0 right-0 h-1/5 bg-gradient-to-t from-blue-200 to-transparent opacity-50"></div>
          
          {/* Grid lines representing roads */}
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(to right, rgba(100,100,100,0.1) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(100,100,100,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>

          {/* Major roads */}
          <div className="absolute top-1/4 left-0 right-0 h-1 bg-amber-300 opacity-60"></div>
          <div className="absolute top-1/2 left-0 right-0 h-2 bg-amber-400 opacity-60"></div>
          <div className="absolute top-3/4 left-0 right-0 h-1 bg-amber-300 opacity-60"></div>
          <div className="absolute left-1/4 top-0 bottom-0 w-1 bg-amber-300 opacity-60"></div>
          <div className="absolute left-1/2 top-0 bottom-0 w-2 bg-amber-400 opacity-60"></div>
          <div className="absolute left-3/4 top-0 bottom-0 w-1 bg-amber-300 opacity-60"></div>

          {/* Area labels */}
          <div className="absolute top-[15%] left-[45%] bg-white/80 px-2 py-0.5 rounded text-xs font-medium text-gray-600">
            Haywards Heath
          </div>
          <div className="absolute top-[40%] left-[40%] bg-white/80 px-2 py-0.5 rounded text-xs font-medium text-gray-600">
            Burgess Hill
          </div>
          <div className="absolute top-[50%] left-[25%] bg-white/80 px-2 py-0.5 rounded text-xs font-medium text-gray-600">
            Hassocks
          </div>
          <div className="absolute top-[55%] left-[55%] bg-white/80 px-2 py-0.5 rounded text-xs font-medium text-gray-600">
            Hurstpierpoint
          </div>
          <div className="absolute top-[80%] left-[35%] bg-white/80 px-2 py-0.5 rounded text-xs font-medium text-gray-600">
            Brighton
          </div>

          {/* Job pins */}
          {filteredLocations.map((job) => {
            const pos = getMapPosition(job.lat, job.lng);
            const color = zoneColors[job.zone] || getStatusColor(job.status);
            
            return (
              <div 
                key={job.id}
                className="absolute transform -translate-x-1/2 -translate-y-full cursor-pointer hover:scale-150 transition-transform z-10"
                style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                title={`${job.customerName}\n${job.zone}\n£${job.price.toFixed(2)}`}
              >
                <div 
                  className="w-3 h-3 rounded-full shadow-lg"
                  style={{ backgroundColor: color }}
                />
              </div>
            );
          })}
        </div>

        {/* Map Controls */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
          <button 
            onClick={() => setShowLayers(!showLayers)}
            className={`p-2.5 bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors ${showLayers ? 'ring-2 ring-emerald-500' : ''}`}
          >
            <Layers size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Zoom Controls */}
        <div className="absolute bottom-4 right-4 flex flex-col gap-1 z-20">
          <button 
            onClick={() => setZoomLevel(z => Math.min(z + 0.2, 2))}
            className="w-10 h-10 bg-white rounded-lg shadow-lg hover:bg-gray-50 flex items-center justify-center transition-colors"
          >
            <ZoomIn size={20} className="text-gray-600" />
          </button>
          <button 
            onClick={() => setZoomLevel(z => Math.max(z - 0.2, 0.6))}
            className="w-10 h-10 bg-white rounded-lg shadow-lg hover:bg-gray-50 flex items-center justify-center transition-colors"
          >
            <ZoomOut size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Layer Panel */}
        {showLayers && (
          <div className="absolute top-4 right-16 bg-white rounded-lg shadow-xl p-4 z-30 w-48">
            <h3 className="font-semibold text-gray-900 mb-3">Map Layers</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" defaultChecked className="rounded text-emerald-500" />
                <span className="text-sm text-gray-700">Today's Jobs</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" defaultChecked className="rounded text-emerald-500" />
                <span className="text-sm text-gray-700">Scheduled</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" defaultChecked className="rounded text-emerald-500" />
                <span className="text-sm text-gray-700">Overdue</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded text-emerald-500" />
                <span className="text-sm text-gray-700">Completed</span>
              </label>
            </div>
            <div className="border-t border-gray-200 mt-3 pt-3">
              <h4 className="text-xs font-semibold text-gray-500 mb-2">LEGEND</h4>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs text-gray-600">Today</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span className="text-xs text-gray-600">Scheduled</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-xs text-gray-600">Overdue</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Bar */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg px-4 py-2 z-20">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <MapPin size={14} className="text-emerald-500" />
              <span className="font-medium">{filteredLocations.length}</span>
              <span className="text-gray-500">jobs</span>
            </div>
            <div className="w-px h-4 bg-gray-200"></div>
            <div className="flex items-center gap-1">
              <Users size={14} className="text-emerald-500" />
              <span className="font-medium">2</span>
              <span className="text-gray-500">staff</span>
            </div>
            <div className="w-px h-4 bg-gray-200"></div>
            <div className="flex items-center gap-1">
              <Calendar size={14} className="text-green-500" />
              <span className="font-medium">Today</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualPlannerPage;
